import { BusinessRulesConditions } from 'sb-lib/types/business-rules-types';

export interface Goal {
  active?: boolean;
  dateCreated: string;
  dateUpdated: string;
  description: string | null;
  enabled: boolean;
  latestVersion?: GoalVersion | null;
  id: number;
  name: string;
  order: number;
  isDefault: boolean;
}

export interface GoalVersion {
  id: number;
  goalId: number;
  graph: GoalGraph;
  dateCreated: string;
  dateUpdated: string;
  conditions?: BusinessRulesConditions | null;
}

export interface GoalResponse {
  data: Goal;
}

export interface GoalListResponse {
  data: Goal[];
}

export interface GoalVersionResponse {
  data: GoalVersion;
}

export interface GoalVersionListResponse {
  data: GoalVersion[];
}

export enum GoalGraphNodeKind {
  ACTIVATE_MODE = 'activate_mode',
  ALERT = 'alert',
  BRANCH = 'branch',
  CONVERSATION_CONTEXT = 'send_system_message',
  QUALIFY = 'qualify',
  SEND_MESSAGE = 'send_message',
  BOOK_MEETING = 'book_meeting',
  ROUTE_FOR_CHAT = 'route_for_chat',
}

export enum GoalMode {
  SALES = 'sales',
  EDUCATION = 'education',
  DEMO = 'demo',
}

export enum GoalGraphStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
}

export enum GoalBehaviors {
  CAN_SCHEDULE = 'scheduling',
}

export enum GoalBehaviorType {
  ALLOWED = 'allowed',
  DENIED = 'denied',
}

export type ActiveGoalModeBehaviors = Record<
  GoalMode,
  Record<GoalBehaviors, GoalBehaviorType>
>;

interface BaseGoalGraphNode {
  kind: GoalGraphNodeKind;
  id?: string;
  children: GoalGraphNode[];
  nextNodeId?: string;
  status?: GoalGraphStatus;
}

export interface GoalActivateMode extends BaseGoalGraphNode {
  kind: GoalGraphNodeKind.ACTIVATE_MODE;
  mode: GoalMode;
  modeBehaviors?: Record<GoalBehaviors, GoalBehaviorType>;
  calendarEventTypeId?: number;
}

export interface GoalAlertIntegrationConfig {
  slackChannelId?: string;
  routeToHubspotOwner?: boolean;
  routeToSalesforceOwner?: boolean;
}

export interface GoalAlert extends BaseGoalGraphNode {
  kind: GoalGraphNodeKind.ALERT;
  title: string;
  body: string;
  oncePerSession: boolean;
  userIds: number[];
  userGroupIds: number[];
  integrationConfig: GoalAlertIntegrationConfig;
}

export interface GoalBusinessRuleConditionsBranch {
  conditions: BusinessRulesConditions;
  childNodeId?: string;
}

export interface GoalBranch extends BaseGoalGraphNode {
  kind: GoalGraphNodeKind.BRANCH;
  conditions: GoalBusinessRuleConditionsBranch[];
  defaultChildNodeId?: string;
}

export interface GoalQualify extends BaseGoalGraphNode {
  kind: GoalGraphNodeKind.QUALIFY;
  conditions: GoalBusinessRuleConditionsBranch[];
  defaultChildNodeId?: string;
}

export interface ConversationContextMessage {
  text: string;
  conditions?: BusinessRulesConditions;
}

export interface GoalConversationContext extends BaseGoalGraphNode {
  kind: GoalGraphNodeKind.CONVERSATION_CONTEXT;
  messages: ConversationContextMessage[];
}

export interface GoalSendMessage extends BaseGoalGraphNode {
  kind: GoalGraphNodeKind.SEND_MESSAGE;
  message: string;
  debug: boolean;
}

export interface GoalBookMeeting extends BaseGoalGraphNode {
  kind: GoalGraphNodeKind.BOOK_MEETING;
  calendarEventTypeId: number;
  message: string;
}

export interface GoalRouteForChat extends BaseGoalGraphNode {
  kind: GoalGraphNodeKind.ROUTE_FOR_CHAT;
  children: GoalGraphNode[];
  falseChildNodeId: string | null;
  trueChildNodeId: string | null;
  waitTime: number;
}

export type GoalGraphNode =
  | GoalActivateMode
  | GoalAlert
  | GoalBranch
  | GoalConversationContext
  | GoalQualify
  | GoalSendMessage
  | GoalBookMeeting
  | GoalRouteForChat;

export interface GoalGraph {
  root: GoalGraphNode;
}
